"use client";

import { capitalizeIconName } from "@/app/utilities/capitalizeIconName";
import { getFormattedDateWithTime } from "@/app/utilities/getFormattedDateWithTime";
import NextImage, { type ImageProps as NextImageProps } from "next/image";
import NextLink, { type LinkProps as NextLinkProps } from "next/link";

import { EvergreenOffering } from "@/composite/EvergreenOffering";
import { EvergreenOfferingCardProps } from "@/composite/EvergreenOffering/EvergreenOfferingCard";
import type { EvergreenCommunityReference, WithTheme } from "@/types";
import { resolveImageProps } from "@/app/utilities/images";

/**
 * EvergreenCommunity block component
 */
export function EvergreenCommunityBlock(
	props: WithTheme<EvergreenCommunityReference>
): JSX.Element {
	/**
	 * Format event card data
	 */
	function mapEventCards(
		cards: EvergreenCommunityReference["event_card"]
	): EvergreenOfferingCardProps[] {
		return cards.map(({ event_card: eventCard }) => ({
			eyebrow: eventCard?.eyebrow,
			headline: eventCard?.title,
			headlineTag: eventCard?.headline_tag,
			subheading: getFormattedDateWithTime(eventCard?.event_date),
			body: eventCard?.description,
			promoText: eventCard?.badge?.description,
			promoIcon: capitalizeIconName(eventCard?.badge?.icons),
			image: {
				component: NextImage,
				...resolveImageProps("16:9", eventCard?.asset)
			},
			primaryCta: eventCard?.call_to_action
				? {
						component: NextLink,
						children: eventCard?.call_to_action[0]?.cta?.title,
						href: eventCard?.call_to_action[0]?.cta?.href,
						target: eventCard?.call_to_action[0]?.open_new_tab
							? "_blank"
							: "_self"
					}
				: undefined,
			secondaryCta: eventCard?.call_to_action
				? {
						component: NextLink,
						children: eventCard?.call_to_action[1]?.cta?.title,
						href: eventCard?.call_to_action[1]?.cta?.href,
						target: eventCard?.call_to_action[1]?.open_new_tab
							? "_blank"
							: "_self"
					}
				: undefined,
			theme: props.theme,
			backgroundShape: eventCard?.theme
		})) as never;
	}

	/**
	 * Format content cards data
	 */
	function mapContentCards(
		cards: EvergreenCommunityReference["content_cards"]
	): EvergreenOfferingCardProps[] {
		return cards.map((card) => ({
			eyebrow: card?.eyebrow,
			headline: card?.content_card_title?.headline,
			headlineTag: card?.content_card_title?.headline_tag,
			subheading: "",
			body: card?.card_description,
			promoText: card?.badge?.description,
			promoIcon: capitalizeIconName(card?.badge?.icon_name),
			image: {
				component: NextImage,
				...resolveImageProps("16:9", card?.image)
			},
			primaryCta: card?.call_to_action[0]
				? {
						component: NextLink,
						children: card?.call_to_action[0]?.link?.title,
						href: card?.call_to_action[0]?.link?.href,
						target: card?.call_to_action[0]?.open_new_tab
							? "_blank"
							: "_self"
					}
				: undefined,
			secondaryCta: card?.call_to_action[1]
				? {
						component: NextLink,
						children: card?.call_to_action[1]?.link?.title,
						href: card?.call_to_action[1]?.link?.href,
						target: card?.call_to_action[1]?.open_new_tab
							? "_blank"
							: "_self"
					}
				: undefined,
			theme: props.theme,
			backgroundShape: card?.theme
		})) as never;
	}

	const eventCards = mapEventCards(props.event_card || []);
	const contentCards = mapContentCards(props.content_cards || []);
	const cards = [...eventCards, ...contentCards];

	return (
		<EvergreenOffering<NextImageProps, NextLinkProps>
			headline={props.title_headline?.headline}
			headlineTag={props.title_headline?.headline_tag}
			description={props.description}
			items={cards as never}
			theme={props.theme || "standard"}
		/>
	);
}
